import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Search, UserCheck, UserX, Shield, User, 
  Eye, MoreVertical, Mail, Map, Calendar
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

export default function AdminUsers() {
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const limit = 20;

  useEffect(() => {
    fetchUsers();
  }, [page, search]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users', {
        params: { skip: page * limit, limit, search: search || undefined }
      });
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      setLoadingDetails(true);
      const response = await api.get(`/admin/users/${userId}`);
      setUserDetails(response.data);
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Erro ao carregar detalhes');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const response = await api.put(`/admin/users/${userId}/toggle-active`);
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const handleChangeRole = async (userId, role) => {
    try {
      const response = await api.put(`/admin/users/${userId}/role?role=${role}`);
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar role');
    }
  };

  const openDetails = (user) => {
    setSelectedUser(user);
    setDetailsOpen(true);
    fetchUserDetails(user.id);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Usuários</h1>
          <p className="text-slate-400">Gerencie todos os usuários da plataforma</p>
        </div>
        <Badge className="bg-blue-500/20 text-blue-400">
          {total} usuários
        </Badge>
      </div>

      {/* Search */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Buscar por nome ou email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">Usuário</TableHead>
                  <TableHead className="text-slate-400">Email</TableHead>
                  <TableHead className="text-slate-400">Role</TableHead>
                  <TableHead className="text-slate-400">Viagens</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Criado em</TableHead>
                  <TableHead className="text-slate-400 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="border-slate-700">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8 bg-slate-700">
                          <AvatarFallback className="text-lg bg-transparent">
                            {user.avatar_emoji || '😊'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-white font-medium">{user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300">{user.email}</TableCell>
                    <TableCell>
                      {user.role === 'admin' ? (
                        <Badge className="bg-orange-500/20 text-orange-400">
                          <Shield className="w-3 h-3 mr-1" />
                          Admin
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-600 text-slate-300">
                          <User className="w-3 h-3 mr-1" />
                          Usuário
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {user.trips_count || 0}
                    </TableCell>
                    <TableCell>
                      {user.is_active ? (
                        <Badge className="bg-green-500/20 text-green-400">Ativo</Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-400">Inativo</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm">
                      {user.created_at 
                        ? format(parseISO(user.created_at), "dd/MM/yyyy", { locale: ptBR })
                        : '-'
                      }
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                          <DropdownMenuItem 
                            className="text-slate-300 focus:text-white focus:bg-slate-700"
                            onClick={() => openDetails(user)}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Ver detalhes
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-slate-300 focus:text-white focus:bg-slate-700"
                            onClick={() => handleToggleActive(user.id)}
                          >
                            {user.is_active ? (
                              <>
                                <UserX className="w-4 h-4 mr-2" />
                                Desativar
                              </>
                            ) : (
                              <>
                                <UserCheck className="w-4 h-4 mr-2" />
                                Ativar
                              </>
                            )}
                          </DropdownMenuItem>
                          {user.role !== 'admin' && (
                            <DropdownMenuItem 
                              className="text-orange-400 focus:text-orange-300 focus:bg-slate-700"
                              onClick={() => handleChangeRole(user.id, 'admin')}
                            >
                              <Shield className="w-4 h-4 mr-2" />
                              Tornar Admin
                            </DropdownMenuItem>
                          )}
                          {user.role === 'admin' && (
                            <DropdownMenuItem 
                              className="text-slate-300 focus:text-white focus:bg-slate-700"
                              onClick={() => handleChangeRole(user.id, 'user')}
                            >
                              <User className="w-4 h-4 mr-2" />
                              Remover Admin
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-400">
            Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, total)} de {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="border-slate-700 text-slate-300"
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="border-slate-700 text-slate-300"
            >
              Próximo
            </Button>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Avatar className="w-10 h-10 bg-slate-700">
                <AvatarFallback className="text-xl bg-transparent">
                  {selectedUser?.avatar_emoji || '😊'}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-bold">{selectedUser?.name}</p>
                <p className="text-sm text-slate-400 font-normal">{selectedUser?.email}</p>
              </div>
            </DialogTitle>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" />
            </div>
          ) : userDetails && (
            <div className="space-y-6 mt-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <Map className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{userDetails.trips?.length || 0}</p>
                  <p className="text-xs text-slate-400">Viagens</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <Calendar className="w-5 h-5 text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{userDetails.organized_trips?.length || 0}</p>
                  <p className="text-xs text-slate-400">Organizadas</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <Mail className="w-5 h-5 text-purple-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold">
                    R$ {(userDetails.payments?.total_paid || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-slate-400">Total Pago</p>
                </div>
              </div>

              {/* Trips List */}
              {userDetails.trips?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 text-slate-300">Viagens Participando</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {userDetails.trips.slice(0, 5).map((trip) => (
                      <div key={trip.id} className="flex items-center justify-between bg-slate-700/30 rounded-lg p-3">
                        <div>
                          <p className="font-medium text-white">{trip.name}</p>
                          <p className="text-sm text-slate-400">{trip.destination || 'Sem destino'}</p>
                        </div>
                        <Badge className={
                          trip.status === 'active' ? 'bg-green-500/20 text-green-400' :
                          trip.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-slate-500/20 text-slate-400'
                        }>
                          {trip.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
